import { SocialIcon } from "../utils/social-icon";
import { ItemWithMetaData, Keyword, Rating, Source } from "../api/Items";
import RatingBadges from "../shared-components/RatingBadges";
import KeywordBadges from "../shared-components/KeywordBadges";
import TopicBadges from "../shared-components/TopicBadges";
import { contentMediaToMediaExternalInternal, PostImage } from "./PostImage";
import { atLeastOneItem } from "../utils";
import PostArticleTranslation from "./PostArticleTranslation";
import {
  getDisplayPostTypeText,
  getSourceDisplayName,
} from "../utils/post-utils";

const WEB_CONTENT_SOURCE_NAME = "Web Content";

export interface PostsSummaryProps {
  content: any;
  ratings: Rating[];
  source: Source | undefined;
  keywords: Keyword[] | undefined;
  item: ItemWithMetaData;
}

function PostSummaryTitle({
  source,
  content,
  item,
}: {
  source: Source | undefined;
  content: any;
  item: ItemWithMetaData;
}) {
  if (source?.name === WEB_CONTENT_SOURCE_NAME) {
    return (
      <span className="inline-block max-w-lg truncate">
        {content?.title ? content?.title ?? content?.url : "Web Content"}
      </span>
    );
  }
  return (
    <span>
      {getDisplayPostTypeText(item)} on{" "}
      {getSourceDisplayName(source?.name ?? "Web Content")}
    </span>
  );
}

const PostsSummary = ({
  content,
  ratings,
  source,
  keywords,
  item,
}: PostsSummaryProps) => {
  return (
    <div className="my-4 border rounded-lg ">
      <div className="flex flex-nowrap py-2 items-center break-inside-avoid">
        <SocialIcon
          className="self-center object-contain h-8 mx-3"
          url={content.url}
        />

        <div className="flex flex-nowrap flex-col text-sm">
          <div className="font-bold pr-2 flex">
            <PostSummaryTitle source={source} content={content} item={item} />
            <a
              className="self-start text-blue-500 text-sm ml-2 font-thin"
              href={content.url}
              rel="noreferrer"
              target="_blank"
            >
              ( View Link )
            </a>
          </div>
          <div className="self-start">{content.created_at}</div>
        </div>
      </div>
      <hr />

      <div className="flex flex-nowrap mt-3 print:flex-col overflow-clip">
        <div className="w-full hidden print:flex print:flex-col">
          <div className="align-top mx-3">{content.text}</div>
          <div id="tags" className="flex flex-col p-3 gap-3">
            {atLeastOneItem(ratings) && (
              <RatingBadges ratings={ratings} showFlagIcon={true} />
            )}
            {atLeastOneItem(keywords) && (
              <KeywordBadges keywords={keywords ?? []} />
            )}
            {atLeastOneItem(item.topics) && (
              <TopicBadges topics={item.topics ?? []} />
            )}
          </div>
        </div>
        <div className="max-w-80 print:max-w-full flex ">
          {content?.media?.length > 0 && (
            <PostImage media={contentMediaToMediaExternalInternal(content)} />
          )}
        </div>

        <div className="flex flex-nowrap flex-col print:hidden">
          <div className=" align-top mx-3">
            <div>{content.text}</div>
            <PostArticleTranslation item={item} isSummary={true} />
          </div>
          <div id="tags" className="flex flex-col p-3 gap-3">
            {atLeastOneItem(ratings) && (
              <RatingBadges ratings={ratings} showFlagIcon={true} />
            )}
            {atLeastOneItem(keywords) && (
              <KeywordBadges keywords={keywords ?? []} />
            )}
            {atLeastOneItem(item.topics) && (
              <TopicBadges topics={item.topics ?? []} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostsSummary;
